import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, FileText, ClipboardList, Users, UserCheck, MessageSquare, ArrowRight, X } from 'lucide-react';
import { adminSearch } from '../lib/api';

const SECTION_CONFIG = {
  quotes: { icon: FileText, label: 'Quotes', color: '#F59E0B', getLink: (r) => `/admin/quotes/${r.quote_id}`, getId: (r) => r.quote_id, getName: (r) => r.company_name || r.contact_name, getEmail: (r) => r.email, getStatus: (r) => r.status },
  orders: { icon: ClipboardList, label: 'Orders', color: '#00D4FF', getLink: (r) => `/admin/orders/${r.id}`, getId: (r) => r.order_number, getName: (r) => r.company_name || r.contact_name, getEmail: (r) => r.email, getStatus: (r) => r.status },
  buyers: { icon: UserCheck, label: 'Buyers', color: '#10B981', getLink: (r) => `/admin/buyers/${r.id}`, getId: (r) => r.company_name, getName: (r) => r.contact_name, getEmail: (r) => r.email, getStatus: (r) => r.role?.replace('buyer_', '') },
  leads: { icon: Users, label: 'Leads', color: '#8B5CF6', getLink: (r) => `/admin/leads/${r.id}`, getId: (r) => r.company_name, getName: (r) => r.contact_name, getEmail: (r) => r.email, getStatus: (r) => r.status },
  contacts: { icon: MessageSquare, label: 'Contacts', color: '#EC4899', getLink: (r) => '/admin/contacts', getId: (r) => r.name, getName: (r) => r.company || '', getEmail: (r) => r.email, getStatus: () => null },
};

export const AdminSearchBar = () => {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);
  const debounceRef = useRef(null);

  const doSearch = useCallback(async (q) => {
    if (!q || q.length < 2) {
      setResults(null);
      return;
    }
    setLoading(true);
    try {
      const res = await adminSearch(q);
      setResults(res.data);
      setOpen(true);
    } catch {
      setResults(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    clearTimeout(debounceRef.current);
    if (query.length >= 2) {
      debounceRef.current = setTimeout(() => doSearch(query), 300);
    } else {
      setResults(null);
    }
    return () => clearTimeout(debounceRef.current);
  }, [query, doSearch]);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Keyboard shortcut: Ctrl+K or Cmd+K
  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        inputRef.current?.focus();
        setOpen(true);
      }
      if (e.key === 'Escape') {
        setOpen(false);
        inputRef.current?.blur();
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  const handleNavigate = (link) => {
    if (link) {
      navigate(link);
      setOpen(false);
      setQuery('');
      setResults(null);
    }
  };

  const totalResults = results ? Object.values(results).reduce((sum, arr) => sum + arr.length, 0) : 0;

  return (
    <div ref={wrapperRef} className="relative w-full max-w-xl" data-testid="admin-search-bar">
      {/* Search Input */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => { if (results) setOpen(true); }}
          placeholder="Search quotes, orders, buyers, leads..."
          className="w-full pl-10 pr-20 py-2.5 bg-white/5 border border-white/10 text-white text-sm placeholder-white/30 focus:outline-none focus:border-[#00D4FF]/50 transition-colors"
          data-testid="admin-search-input"
        />
        <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-2">
          {loading && <div className="w-4 h-4 border-2 border-[#00D4FF]/30 border-t-[#00D4FF] rounded-full animate-spin" />}
          {query && (
            <button onClick={() => { setQuery(''); setResults(null); setOpen(false); }} className="p-0.5 text-white/30 hover:text-white">
              <X className="w-3.5 h-3.5" />
            </button>
          )}
          <kbd className="hidden sm:inline-block px-1.5 py-0.5 text-[10px] text-white/20 border border-white/10 font-mono">⌘K</kbd>
        </div>
      </div>

      {/* Results Dropdown */}
      {open && results && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-[#0A0A0A] border border-white/10 shadow-2xl z-50 max-h-[70vh] overflow-y-auto" data-testid="search-results-dropdown">
          {totalResults === 0 ? (
            <div className="p-6 text-center text-white/40 text-sm" data-testid="search-no-results">
              No results for "{query}"
            </div>
          ) : (
            Object.entries(SECTION_CONFIG).map(([key, config]) => {
              const items = results[key];
              if (!items || items.length === 0) return null;
              const Icon = config.icon;
              return (
                <div key={key} data-testid={`search-section-${key}`}>
                  <div className="px-4 py-2 flex items-center gap-2 border-b border-white/5 bg-white/[0.02]">
                    <Icon className="w-3.5 h-3.5" style={{ color: config.color }} />
                    <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: config.color }}>{config.label}</span>
                    <span className="text-xs text-white/20 ml-auto">{items.length}</span>
                  </div>
                  {items.map((item, i) => {
                    const link = config.getLink(item);
                    const id = config.getId(item);
                    const name = config.getName(item);
                    const email = config.getEmail(item);
                    const status = config.getStatus(item);
                    return (
                      <button
                        key={i}
                        onClick={() => handleNavigate(link || `/admin/${key}`)}
                        className="w-full px-4 py-3 flex items-center gap-3 hover:bg-white/5 transition-colors text-left group"
                        data-testid={`search-result-${key}-${i}`}
                      >
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="text-white font-medium text-sm truncate">{id}</span>
                            {name && name !== id && <span className="text-white/40 text-xs truncate">{name}</span>}
                          </div>
                          {email && <div className="text-white/30 text-xs truncate">{email}</div>}
                        </div>
                        {status && (
                          <span className="shrink-0 px-2 py-0.5 text-[10px] uppercase tracking-wider font-bold" style={{ backgroundColor: `${config.color}15`, color: config.color }}>
                            {status.replace(/_/g, ' ')}
                          </span>
                        )}
                        <ArrowRight className="w-3.5 h-3.5 text-white/10 group-hover:text-white/40 shrink-0 transition-colors" />
                      </button>
                    );
                  })}
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
};
